import {
  compareSeq,
  maxSeq,
  type ConversationEvent,
  type ConversationSummary,
  type FileInfo,
  type LinkPreview,
  type Message,
  type MessagePreview,
  type MessageReactions,
  type Seq,
} from '@sd/contracts';
import type { Timeline } from './state';

/**
 * Merges messages by id keeping ascending seq order. A newer revision (or a
 * tombstone) replaces the stored copy; reactions go by their own version, so
 * an edit's copy cannot bring back older reactions nor a page older than the
 * last reaction event. Replays and out-of-order deliveries are harmless.
 */
export function upsertMessages(list: readonly Message[], incoming: readonly Message[]): Message[] {
  if (!incoming.length) return list as Message[];
  const byId = new Map(list.map((m) => [m.id, m]));
  let changed = false;
  for (const message of incoming) {
    const current = byId.get(message.id);
    const next = current ? mergeMessage(current, message) : message;
    if (next !== current) {
      byId.set(message.id, next);
      changed = true;
    }
  }
  if (!changed) return list as Message[];
  return [...byId.values()].sort((a, b) => compareSeq(a.seq, b.seq));
}

function mergeMessage(current: Message, next: Message): Message {
  const base = isNewer(next, current) ? { ...next, receipt: next.receipt ?? current.receipt, ...previewsKept(next, current) } : current;
  if (base.deletedAt) return base;
  const source = newerReactions(next, current) ? next : current;
  if (base.reactions === source.reactions && base.reactionEventSeq === source.reactionEventSeq) return base;
  return { ...base, reactions: source.reactions, reactionEventSeq: source.reactionEventSeq };
}

/** A copy without a version comes from a server without reactions, or from a message nobody reacted to. */
function newerReactions(next: Pick<Message, 'reactionEventSeq'>, current: Pick<Message, 'reactionEventSeq'>): boolean {
  if (next.reactionEventSeq === undefined) return false;
  return current.reactionEventSeq === undefined || compareSeq(next.reactionEventSeq, current.reactionEventSeq) > 0;
}

/** A reaction event or answer: kept when newer than the copy in the list, never on a deleted message or one not loaded. */
export function applyReactionState(list: readonly Message[], state: MessageReactions): Message[] {
  let changed = false;
  const next = list.map((m) => {
    if (m.id !== state.messageId || m.deletedAt || !newerReactions(state, m)) return m;
    changed = true;
    return { ...m, reactions: state.reactions, reactionEventSeq: state.reactionEventSeq };
  });
  return changed ? next : (list as Message[]);
}

/** Newer copies of messages already in the list; others are left out. */
export function upsertLoaded(list: readonly Message[], incoming: readonly Message[]): Message[] {
  const loaded = new Set(list.map((m) => m.id));
  return upsertMessages(list, incoming.filter((m) => loaded.has(m.id)));
}

/**
 * Link previews of a stored message change only through `message.link_previews` events (or the
 * author's removal): a page fetched before a card arrived must not take it away again. A new
 * revision or a deletion brings the server's current cards.
 */
function previewsKept(next: Message, current: Message): Pick<Message, 'linkPreviews'> {
  return next.revision === current.revision && !next.deletedAt ? { linkPreviews: current.linkPreviews } : {};
}

/** The message with these cards, wherever it is loaded. */
export function setLinkPreviews(list: readonly Message[], messageId: string, linkPreviews: LinkPreview[]): Message[] {
  let changed = false;
  const next = list.map((m) => {
    if (m.id !== messageId || m.deletedAt) return m;
    changed = true;
    return { ...m, linkPreviews };
  });
  return changed ? next : (list as Message[]);
}

function isNewer(next: Message, current: Message): boolean {
  if (next.deletedAt && !current.deletedAt) return true;
  if (current.deletedAt && !next.deletedAt) return false;
  if (next.revision !== current.revision) return next.revision > current.revision;
  // Same revision: accept refreshed receipts / attachment scan states.
  return JSON.stringify(next.receipt) !== JSON.stringify(current.receipt) || JSON.stringify(next.attachments) !== JSON.stringify(current.attachments);
}

/** Replaces attachment metadata (scan result) wherever the file is referenced. */
function updateFileInMessages(list: readonly Message[], file: FileInfo): Message[] {
  let changed = false;
  const next = list.map((m) => {
    if (!m.attachments.some((a) => a.id === file.id)) return m;
    changed = true;
    return { ...m, attachments: m.attachments.map((a) => (a.id === file.id ? file : a)) };
  });
  return changed ? next : (list as Message[]);
}

/** Applies an event once: events at or below the timeline's cursor are already in it. */
export function applyEventToTimeline(timeline: Timeline, event: ConversationEvent): Timeline {
  if (compareSeq(event.eventSeq, timeline.eventCursor) <= 0) return timeline;
  let messages = timeline.messages;
  const data = event.data;
  switch (data.type) {
    case 'message.created':
      // A slice opened around an older message gets newer ones by paging (loadNewer): appending here would leave a gap.
      if (!timeline.hasMoreAfter && compareSeq(data.message.seq, timeline.historyStartSeq) >= 0) messages = upsertMessages(messages, [data.message]);
      break;
    case 'message.updated':
    case 'message.deleted':
      // Outside the loaded pages it is fetched fresh when paged in; inserting it would leave a gap.
      messages = upsertLoaded(messages, [data.message]);
      break;
    case 'file.updated':
      messages = updateFileInMessages(messages, data.file);
      break;
    case 'reaction.updated':
      messages = applyReactionState(messages, data);
      break;
    case 'message.link_previews':
      messages = setLinkPreviews(messages, data.messageId, data.linkPreviews);
      break;
    default:
      break;
  }
  return { ...timeline, messages, eventCursor: maxSeq(timeline.eventCursor, event.eventSeq) };
}

/** Builds a list preview from a message (used when events arrive between list refreshes). */
export function previewOf(message: Message): MessagePreview {
  const deleted = Boolean(message.deletedAt);
  const kind: MessagePreview['kind'] = deleted
    ? 'deleted'
    : message.kind === 'system'
      ? 'system'
      : !message.text?.trim() && message.attachments.length
        ? 'attachment'
        : 'text';
  return {
    messageId: message.id,
    senderId: message.senderId,
    kind,
    text: deleted ? null : message.text ? message.text.slice(0, 140) : null,
    system: message.system,
    attachmentName: message.attachments[0]?.name ?? null,
    attachmentCount: message.attachments.length,
    createdAt: message.createdAt,
  };
}

export interface SummaryEventContext {
  meId: string;
  /** The viewer is looking at the bottom of this conversation right now. */
  viewing: boolean;
}

/**
 * Applies a conversation event to its list summary. Events at or below the
 * summary's cursor are already reflected in it, so replays change nothing.
 */
export function applyEventToSummary(summary: ConversationSummary, event: ConversationEvent, ctx: SummaryEventContext): ConversationSummary {
  if (compareSeq(event.eventSeq, summary.lastEventSeq) <= 0) return summary;
  const next: ConversationSummary = { ...summary, lastEventSeq: event.eventSeq };
  const data = event.data;
  switch (data.type) {
    case 'message.created': {
      const m = data.message;
      if (compareSeq(m.seq, summary.my.historyStartSeq) < 0) return next;
      next.lastMessage = previewOf(m);
      next.lastSeq = maxSeq(summary.lastSeq, m.seq);
      if (m.createdAt > next.lastActivityAt) next.lastActivityAt = m.createdAt;
      if (m.kind === 'text' && m.senderId !== ctx.meId && !ctx.viewing && compareSeq(m.seq, summary.my.lastReadSeq) > 0) {
        next.unreadCount = summary.unreadCount + 1;
        if (m.mentions.includes(ctx.meId)) next.mentioned = true;
      }
      if (m.senderId === ctx.meId) {
        // The server advances the sender's read pointer when it stores the message.
        next.my = { ...summary.my, lastReadSeq: maxSeq(summary.my.lastReadSeq, m.seq) };
      }
      // A message from someone else stays unread here even while the chat is on screen:
      // only the server's markRead answer moves the pointer, otherwise the server (and
      // the sender's receipt) would never learn that it was read.
      return next;
    }
    case 'message.updated':
    case 'message.deleted':
      if (summary.lastMessage?.messageId === data.message.id) next.lastMessage = previewOf(data.message);
      return next;
    case 'read.updated':
      if (data.userId === ctx.meId) {
        next.my = { ...summary.my, lastReadSeq: maxSeq(summary.my.lastReadSeq, data.lastReadSeq) };
        if (compareSeq(data.lastReadSeq, summary.lastSeq) >= 0) {
          next.unreadCount = 0;
          next.mentioned = false;
        }
      }
      return next;
    case 'member.joined':
      next.memberCount = summary.memberCount + data.userIds.filter((id) => id !== ctx.meId).length;
      return next;
    case 'member.left':
    case 'member.removed':
      if (data.userId !== ctx.meId) next.memberCount = Math.max(0, summary.memberCount - 1);
      return next;
    case 'member.role_changed':
      // What the viewer may manage in the group (rename, invite, remove) follows their role at once.
      if (data.userId === ctx.meId) next.my = { ...summary.my, role: data.role };
      return next;
    case 'conversation.updated':
      if (data.title !== undefined) next.title = data.title;
      if (data.historyMode !== undefined) next.historyMode = data.historyMode;
      return next;
    default:
      return next;
  }
}

/**
 * Merges a summary snapshot (list, detail or an action's response) into the
 * stored one. The copy further along the conversation's event stream wins and
 * the read pointer never moves back, so a response that raced with newer
 * events cannot roll back the last message or the unread count.
 */
export function mergeSummary(current: ConversationSummary | undefined, incoming: ConversationSummary): ConversationSummary {
  if (!current) return incoming;
  // The same summary again (a reconnect lists every conversation): the copy on screen stays, so nothing that shows
  // it is drawn again. On a phone each reconnect redrew every message of the open conversation.
  if (JSON.stringify(current) === JSON.stringify(incoming)) return current;
  const [base, other] = compareSeq(incoming.lastEventSeq, current.lastEventSeq) < 0 ? [current, incoming] : [incoming, current];
  if (compareSeq(other.my.lastReadSeq, base.my.lastReadSeq) <= 0) return base;
  // The other copy knows a later read, e.g. a markRead answer the snapshot predates; its count is the fresher one.
  const lastReadSeq = other.my.lastReadSeq;
  const unreadCount = compareSeq(lastReadSeq, base.lastSeq) >= 0 ? 0 : Math.min(base.unreadCount, other.unreadCount);
  return { ...base, my: { ...base.my, lastReadSeq }, unreadCount, mentioned: unreadCount > 0 && base.mentioned && other.mentioned };
}

/** Where `event` falls after an applied `cursor`: already applied, the next one, or past a gap. */
export function eventPosition(cursor: Seq, event: ConversationEvent): 'applied' | 'next' | 'gap' {
  const seq = BigInt(event.eventSeq);
  const applied = BigInt(cursor);
  return seq <= applied ? 'applied' : seq === applied + 1n ? 'next' : 'gap';
}
